import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminSecurity() {
    const { user, refresh } = useAuth();
    const [status, setStatus] = useState(null);
    const [audit, setAudit] = useState([]);
    const [setup, setSetup] = useState(null); // {secret, qr_data_url, otpauth_uri}
    const [code, setCode] = useState("");

    const load = useCallback(async () => {
        try {
            const [s, a] = await Promise.all([
                api.get("/admin/security-status"),
                api.get("/admin/audit-log?limit=50").catch(() => ({ data: { items: [] } })),
            ]);
            setStatus(s.data);
            setAudit(a.data.items);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function startMfa() {
        try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
        catch (e) { toast.error(formatApiError(e)); }
    }
    async function verifyMfa() {
        try {
            await api.post("/auth/mfa/verify", { code });
            toast.success("MFA enabled");
            setSetup(null); setCode("");
            await refresh();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }
    async function disableMfa() {
        const c = prompt("Enter your current MFA code to disable:");
        if (!c) return;
        try { await api.post("/auth/mfa/disable", { code: c }); toast.success("MFA disabled"); await refresh(); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }

    if (!status) return <div className="text-gray-500">Loading…</div>;

    return (
        <div data-testid="admin-security">
            <div className="gsf-eyebrow">Security</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Security dashboard</h1>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Active admins" value={status.active_admins} testid="sec-active-admins" />
                <Stat label="MFA-enrolled" value={status.mfa_enrolled_admins} testid="sec-mfa-count" />
                <Stat label="Failed logins (24h)" value={status.failed_logins_24h} testid="sec-fail-24h" />
                <Stat label="Email provider" value={status.email_provider_configured ? "Configured" : "Not configured"} testid="sec-email-config" warn={!status.email_provider_configured} />
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="gsf-card p-6" data-testid="mfa-panel">
                    <div className="flex items-center gap-2">
                        {user?.mfa_enabled ? <ShieldCheck className="text-green-600" size={22} /> : <ShieldAlert className="text-amber-600" size={22} />}
                        <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">Multi-Factor Authentication</h3>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                        MFA adds a second verification step at login. Use any TOTP authenticator app (Google Authenticator, 1Password, Authy).
                    </p>
                    {user?.mfa_enabled ? (
                        <button onClick={disableMfa} className="mt-4 gsf-btn-outline text-sm" data-testid="mfa-disable-btn">Disable MFA</button>
                    ) : setup ? (
                        <div className="mt-4">
                            <img src={setup.qr_data_url} alt="MFA QR" className="w-40 h-40 border border-gray-200 rounded-lg" data-testid="mfa-qr" />
                            <p className="mt-3 text-xs text-gray-500">Or paste this secret manually: <code className="bg-gray-100 px-1.5 py-0.5 rounded" data-testid="mfa-secret">{setup.secret}</code></p>
                            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="mfa-code" className="mt-3 rounded-lg border border-gray-200 px-3 py-2 text-center tracking-widest" />
                            <button onClick={verifyMfa} className="mt-3 gsf-btn-primary text-sm ml-2" data-testid="mfa-verify-btn">Verify & Enable</button>
                        </div>
                    ) : (
                        <button onClick={startMfa} className="mt-4 gsf-btn-primary text-sm" data-testid="mfa-start-btn">Enable MFA</button>
                    )}
                </div>

                <div className="gsf-card p-6" data-testid="security-checklist">
                    <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">Security posture</h3>
                    <ul className="mt-4 space-y-2 text-sm">
                        <ChecklistItem ok label="HTTPS enforced (production)" />
                        <ChecklistItem ok label="httpOnly session cookies" />
                        <ChecklistItem ok label="Password hashing (bcrypt cost 12)" />
                        <ChecklistItem ok label="Brute-force lockout & rate limiting" />
                        <ChecklistItem ok label="Audit logging enabled" />
                        <ChecklistItem ok label="Security response headers" />
                        <ChecklistItem ok={status.brand_logo_uploaded && status.brand_icon_uploaded} label="Official brand assets uploaded" />
                        <ChecklistItem ok={status.email_provider_configured} label="Email delivery provider configured" warnText="Add RESEND_API_KEY to backend .env" />
                        <ChecklistItem ok={status.mfa_enrolled_admins > 0} label="At least one MFA-enrolled admin" />
                    </ul>
                </div>
            </div>

            <div className="mt-10 gsf-card overflow-x-auto" data-testid="audit-log">
                <div className="p-6 border-b border-gray-100 flex justify-between">
                    <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">Audit log (recent)</h3>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-[#f8faf7] text-left"><tr><th className="p-3">When</th><th className="p-3">Event</th><th className="p-3">Actor</th><th className="p-3">IP</th></tr></thead>
                    <tbody>
                        {audit.length === 0 ? (
                            <tr><td colSpan={4} className="p-6 text-gray-500 text-center">No events yet.</td></tr>
                        ) : audit.map((a) => (
                            <tr key={a.id} className="border-t border-gray-100">
                                <td className="p-3 text-gray-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                                <td className="p-3 font-mono text-xs">{a.event}</td>
                                <td className="p-3">{a.actor_email || "—"}</td>
                                <td className="p-3 text-gray-500">{a.ip || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ label, value, testid, warn }) {
    return (
        <div className={`gsf-card p-6 ${warn ? "border-l-4 border-l-amber-400" : ""}`} data-testid={testid}>
            <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
            <div className="mt-3 font-serif text-3xl font-bold text-[color:var(--gsf-green-700)]">{value}</div>
        </div>
    );
}

function ChecklistItem({ ok, label, warnText }) {
    return (
        <li className="flex items-start gap-3">
            <span className={`mt-1 w-2 h-2 rounded-full ${ok ? "bg-green-500" : "bg-amber-500"}`} />
            <div>
                <div className={ok ? "text-[color:var(--gsf-charcoal)]" : "text-amber-700"}>{label}</div>
                {!ok && warnText && <div className="text-xs text-amber-600">{warnText}</div>}
            </div>
        </li>
    );
}
